import { useEffect, useRef, useState } from "react";
import { message, Modal } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryCache } from "react-query";
import CustomInputField from "../../../../../CustomHTMLElements/CustomInputField";
import {
  deleteData,
  postFormDataWithDotNet,
} from "../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../api/ajax-endpoints";
import styles from "../../LimitedLiability/LimitedLiability.module.scss";
import { ICustomerKycDocument } from "../../../../../../interfaces/businessProfile";
import { ICorporateCustomer } from "../../../hooks/useCorporateCustomers";
import { errorHandler } from "../../../../../../helpers/errorHandler";
import { MAX_DOCUMENT_SIZE, bytesToMB } from "../../../interfaces";
import { documentApprovalStatus } from "../../../../../../components/AggregatorOnboardingReview/util";
import useDocumentViewer from "../../../../../../NewComponents/CustomViewDocumentHandler/CustomViewDocumentHandler";

const schema = yup.object().shape({});

const COMPLETE_ONBOARDING = 3;

interface IBusinessDocuments {
  handleProceed: (step: number) => void;
  formData: any;
  documentTypes: ICustomerKycDocument[];
  resumeCustomerData?: ICorporateCustomer;
}

const BusinessDocuments: React.FC<IBusinessDocuments> = ({
  handleProceed,
  formData,
  documentTypes,
  resumeCustomerData,
}) => {
  const queryCache = useQueryCache();
  const { handleViewDocument, handleViewLocalFile } = useDocumentViewer();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPickingDocument, setIsPickingDocument] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [documentFiles, setDocumentFiles] = useState<
    Array<{ code: string; file: File; isDocumentNew: boolean }>
  >([]);

  const formRef = useRef<HTMLFormElement>(null);
  const itemsRef = useRef<Array<HTMLInputElement | null>>([]);

  const customerId = (formData?.funder_id ||
    resumeCustomerData?.customer_id) as number | undefined;

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, documentTypes?.length ?? 0);
  }, [documentTypes?.length]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!documentTypes?.length) return;
    documentTypes.forEach((doc) => {
      const existingFile = doc.customer_documents?.[0]?.files?.[0];
      if (existingFile?.file_name) {
        setValue(doc.document_field_code, existingFile.file_name);
      }
    });
  }, [documentTypes, setValue]);

  const onChangeFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    documentCode: string,
  ) => {
    if (isPickingDocument) return;
    setIsPickingDocument(true);

    try {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_DOCUMENT_SIZE) {
        message.error(
          `File size must be less than ${bytesToMB(MAX_DOCUMENT_SIZE)}MB`,
        );
        return;
      }

      const allowedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        message.error("Only PNG, JPG, JPEG, and PDF files are allowed");
        return;
      }

      setValue(documentCode, file.name);
      setDocumentFiles((prev) => {
        const filtered = prev.filter((item) => item.code !== documentCode);
        return [...filtered, { code: documentCode, file, isDocumentNew: true }];
      });
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to upload file");
    } finally {
      setIsPickingDocument(false);
    }
  };

  const onSubmit = async (_values: any) => {
    setIsSubmitting(true);

    try {
      const newFiles = documentFiles.filter((item) => item.isDocumentNew);

      if (newFiles.length === 0) {
        handleProceed(COMPLETE_ONBOARDING);
        return;
      }

      const form = new FormData();
      newFiles.forEach((item, index) => {
        form.append(`CustomerDocuments[${index}].Document`, item.file);
        form.append(`CustomerDocuments[${index}].DocumentTypeCode`, item.code);
      });
      form.append(
        "CustomerId",
        formData?.funder_id || resumeCustomerData?.customer_id,
      );

      const response = await postFormDataWithDotNet(
        ajaxEndpoints.UPLOAD_CUSTOMER_DOCUMENT,
        form,
      );

      message.success(
        response?.data?.message ||
          response?.message ||
          "Business documents uploaded successfully!",
      );
      queryCache.invalidateQueries([
        { customerId },
        "getCustomerDocumentsForAdmin",
      ]);
      setDocumentFiles([]);
      handleProceed(COMPLETE_ONBOARDING);
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to upload documents");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewExistingDocument = (uploadPath: string) => {
    handleViewDocument(false, uploadPath);
  };

  const handleDeleteDocument = (
    documentId: number,
    documentCode: string,
    documentName: string,
  ) => {
    Modal.confirm({
      title: "Delete Document",
      content: `Are you sure you want to delete "${documentName}"? This cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      zIndex: 10001,
      onOk: async () => {
        setIsDeleting(true);
        try {
          const response = await deleteData(
            `${ajaxEndpoints.AGGREGATOR_DOCUMENT_UPLOAD}${documentId}?customer_id=${customerId}`,
            {},
          );
          if (response?.status === 200 || response?.data?.status_code === 200) {
            message.success("Document deleted successfully.");
            queryCache.invalidateQueries([
              { customerId },
              "getCustomerDocumentsForAdmin",
            ]);
            setValue(documentCode, "");
          } else {
            message.error(
              response?.data?.message || "Failed to delete document.",
            );
          }
        } catch (error: any) {
          message.error(
            errorHandler(error) || "An error occurred while deleting.",
          );
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  return (
    <div className="mt-5">
      <form
        onSubmit={handleSubmit(onSubmit)}
        ref={formRef}
        className={styles.businessForm}
      >
        {documentTypes?.map((doc, index) => {
          const existingFile = doc.customer_documents?.[0]?.files?.[0] ?? null;
          const newFileEntry = documentFiles.find(
            (f) => f.code === doc.document_field_code && f.isDocumentNew,
          );
          const canDelete =
            !!existingFile &&
            existingFile.approval_status !== documentApprovalStatus.APPROVED &&
            !newFileEntry;
          const canView = !!existingFile || !!newFileEntry;

          return (
            <div key={doc.document_field_id}>
              <CustomInputField
                type="text"
                label={`${doc.document_field_Name} *`}
                name={doc.document_field_code}
                placeholder=""
                reference={register}
                errors={errors[doc.document_field_code]}
                readOnly
              />
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                {!canDelete && (
                  <button
                    type="button"
                    className="btn btn-sm advancly-btn d-flex align-items-center gap-1"
                    onClick={() => itemsRef.current[index]?.click()}
                  >
                    <i
                      className={`fas ${existingFile || documentFiles.find((f) => f.code === doc.document_field_code) ? "fa-sync-alt" : "fa-cloud-upload-alt"}`}
                    />
                    <span>
                      {existingFile ||
                      documentFiles.find(
                        (f) => f.code === doc.document_field_code,
                      )
                        ? "Replace"
                        : "Choose File"}
                    </span>
                  </button>
                )}
                {canView && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    onClick={() => {
                      if (newFileEntry) {
                        handleViewLocalFile(newFileEntry.file);
                      } else if (existingFile?.upload_path) {
                        handleViewExistingDocument(existingFile.upload_path);
                      }
                    }}
                  >
                    <i className="fas fa-eye" />
                    <span>View</span>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                    disabled={isDeleting}
                    onClick={() =>
                      handleDeleteDocument(
                        existingFile.customer_document_id,
                        doc.document_field_code,
                        existingFile.file_name,
                      )
                    }
                  >
                    <i
                      className={`fas ${isDeleting ? "fa-spinner fa-spin" : "fa-trash-alt"}`}
                    />
                    <span>{isDeleting ? "Removing..." : "Remove"}</span>
                  </button>
                )}
              </div>
              <input
                type="file"
                className="d-none"
                ref={(el) => (itemsRef.current[index] = el)}
                onChange={(e) => onChangeFile(e, doc.document_field_code)}
                accept="image/png, image/jpg, image/jpeg, application/pdf"
              />
            </div>
          );
        })}

        <div className={styles.ActionButtonContainer}>
          <button
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            className="btn advancly-btn btn-lg"
            disabled={isSubmitting || isDeleting}
          >
            {isSubmitting ? "Uploading..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => handleProceed(COMPLETE_ONBOARDING)}
            className="btn advancly-btn btn-lg"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessDocuments;
